/**
 * AutomationDropdown
 *
 * Navbar popover showing automation rule stats as a metered dashboard panel.
 * Follows the BatchProcessesDropdown pattern for layout and offset calculation.
 */

import { useState, useRef, useEffect } from 'react';
import { Button, Center, Group, Popover, Skeleton, Stack, Text, UnstyledButton, rem } from '@mantine/core';
import { IconRoute2, IconClock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { useAutomationSharedSelector, selectors } from '../../state/store';
import { CATEGORY_CONFIG } from '../../../constants';
import classes from './AutomationDropdown.module.css';

export function AutomationDropdown() {
  const { t } = useTranslation( 'automation' );
  const navigate = useNavigate();
  const { backend } = ( useBackend() as BackendContextType ) ?? {};
  const buttonRef = useRef<HTMLButtonElement>( null );
  const [ isOpen, setIsOpen ] = useState( false );
  const [ popoverOffset, setPopoverOffset ] = useState({ crossAxis: 0, mainAxis: 0 });

  // Live count of in-flight execution batches (status === 'running'). Fetched
  // when the popover opens. `null` = not yet loaded.
  const [ runningCount, setRunningCount ] = useState<number | null>( null );

  const activeRulesCount = useAutomationSharedSelector( selectors.activeRulesCount );
  const totalRulesCount = useAutomationSharedSelector( selectors.totalRulesCount );
  const rulesByCategory = useAutomationSharedSelector( selectors.rulesByCategory );
  const isLoading = useAutomationSharedSelector(( s ) => s.loading.rules );

  useEffect(() => {
    if ( !backend || !isOpen ) return;
    let cancelled = false;
    ( async () => {
      try {
        const result = await backend.queries.RuleExecutionBatchesSearch({
          filter: [{ field: 'status', op: 'eq', value: 'running' }],
          limit: 1
        });
        if ( !cancelled ) setRunningCount( Number( result?.count ) || 0 );
      } catch {
        if ( !cancelled ) setRunningCount( 0 );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ backend, isOpen ]);

  useEffect(() => {
    const calculateOffset = () => {
      if ( buttonRef.current ) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const distanceToRightEdge = window.innerWidth - buttonRect.right;
        setPopoverOffset({ crossAxis: distanceToRightEdge, mainAxis: 10 });
      }
    };

    calculateOffset();
    window.addEventListener( 'resize', calculateOffset );

    return () => {
      window.removeEventListener( 'resize', calculateOffset );
    };
  }, []);

  const handleGoToAutomation = () => {
    setIsOpen( false );
    navigate( '/automation' );
  };

  const hasRules = totalRulesCount > 0;
  const inactiveCount = totalRulesCount - activeRulesCount;

  const categoryEntries = Object.entries( CATEGORY_CONFIG ).filter(
    ([key]) => ( rulesByCategory[key] || 0 ) > 0
  );

  return (
    <Popover
      position="bottom-end"
      withArrow
      shadow="md"
      width={280}
      withinPortal={true}
      offset={popoverOffset}
      opened={isOpen}
      onChange={setIsOpen}
    >
      <Popover.Target>
        <UnstyledButton
          ref={buttonRef}
          onClick={() => setIsOpen( !isOpen )}
        >
          <Center>
            <IconRoute2
              className={`${classes.icon} ${isOpen ? classes.activeIcon : ''}`}
              style={{ width: rem( 18 ), height: rem( 18 ), marginBottom: '1px' }}
              stroke={1.5}
            />
          </Center>
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown className={classes.dropdown} p={0}>
        <div className={classes.header}>
          <Text size="xs" fw={500} px="xs" py={6}>
            {t( 'automation_dropdown_title' )}
          </Text>
        </div>

        {isLoading ? (
          <div className={classes.statsSection}>
            <Stack gap="sm">
              <Group gap="sm">
                <Skeleton height={40} width={80} radius="sm" />
                <Skeleton height={40} width={80} radius="sm" />
                <Skeleton height={40} width={80} radius="sm" />
              </Group>
              <Skeleton height={12} width="60%" radius="xs" />
              <Stack gap={4}>
                <Skeleton height={10} width="100%" radius="xs" />
                <Skeleton height={10} width="80%" radius="xs" />
                <Skeleton height={10} width="70%" radius="xs" />
              </Stack>
            </Stack>
          </div>
        ) : hasRules ? (
          <>
            {/* Meter gauges row */}
            <div className={classes.statsSection}>
              <Group gap="sm" grow>
                <div className={classes.meterCard}>
                  <Text className={classes.meterValue}>{totalRulesCount}</Text>
                  <Text className={classes.meterLabel}>{t( 'automation_dropdown_meter_total', 'Total' )}</Text>
                </div>
                <div className={classes.meterCardActive}>
                  <Text className={classes.meterValueActive}>{activeRulesCount}</Text>
                  <Text className={classes.meterLabel}>{t( 'automation_dropdown_meter_active', 'Activas' )}</Text>
                </div>
                <div className={classes.meterCard}>
                  <Text className={classes.meterValue}>{inactiveCount}</Text>
                  <Text className={classes.meterLabel}>{t( 'automation_dropdown_meter_inactive', 'Inactivas' )}</Text>
                </div>
              </Group>

              {categoryEntries.length > 0 && (
                <Stack gap={2} mt="sm">
                  <Text className={classes.sectionTitle} mb={2}>
                    {t( 'automation_dropdown_by_category' )}
                  </Text>
                  {categoryEntries.map(([ key, config ]) => {
                    const CategoryIcon = config.icon;
                    const count = rulesByCategory[key] || 0;
                    return (
                      <div key={key} className={classes.categoryRow}>
                        <CategoryIcon size={14} stroke={1.5} className={classes.categoryIcon} />
                        <Text className={classes.categoryLabel}>
                          {t( config.labelKey )}
                        </Text>
                        <Text className={classes.categoryCount}>
                          {count}
                        </Text>
                      </div>
                    );
                  })}
                </Stack>
              )}
            </div>

            <div className={classes.section}>
              <Text className={classes.sectionTitle} mb={4}>
                {t( 'automation_dropdown_executions_title' )}
              </Text>
              <div className={classes.executionPlaceholder}>
                <IconClock
                  size={14}
                  stroke={1.5}
                  style={{
                    color:
                      runningCount && runningCount > 0
                        ? 'var(--mantine-color-yellow-6)'
                        : 'var(--mantine-color-dimmed)'
                  }}
                />
                <Text fz="xs" c={runningCount && runningCount > 0 ? undefined : 'dimmed'}>
                  {runningCount === null
                    ? t( 'automation_dropdown_executions_loading', 'Cargando…' )
                    : runningCount > 0
                      ? t( 'automation_dropdown_executions_running', '{{count}} en ejecución', {
                          count: runningCount
                        })
                      : t( 'automation_dropdown_executions_idle', 'Sin ejecuciones en curso' )}
                </Text>
              </div>
            </div>
          </>
        ) : (
          <div className={classes.emptyStateWrapper}>
            <div className={classes.emptyStateIcon}>
              <IconRoute2 size={24} stroke={1.5} />
            </div>
            <Text fz="sm" fw={500} ta="center">
              {t( 'automation_empty_no_rules' )}
            </Text>
            <Text fz="xs" c="dimmed" ta="center">
              {t( 'automation_empty_no_rules_hint' )}
            </Text>
          </div>
        )}

        <div className={classes.footer}>
          <Button fullWidth variant="subtle" size="xs" onClick={handleGoToAutomation}>
            {t( 'automation_dropdown_go_to' )}
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
